import React, { createContext, useContext } from 'react';
import { usePlayedVideo } from 'system/videotime';
import { useSessionData } from 'util/datastore';

import type { VideoPlayerState } from './videoplayer-types';

// Custom VideoContext. It should provide all necessary data from a played video
export const VideoPlayerStateContext = createContext<VideoPlayerState>({
    ready: false,
    currentTime: 0,
    duration: 0,
} as VideoPlayerState);

/**
 * Custom hook to access the video player state and actions.;
 *
 * @returns {VideoPlayerState} The current state and actions of the video
 * player.
 * @throws {Error} If the hook is not used within a
 * VideoPlayerStateContextProvider.
 */
export function useVideo(): VideoPlayerState {
    const context = useContext(VideoPlayerStateContext);
    return (
        context ?? {
            currentTime: 0,
            paused: false,
            ready: false,
            duration: 0,
        }
    );
}

export type VideoPlayerStateContextProviderProps = { children: React.ReactNode; videoKey?: string; useVideoFromArticle?: boolean };
export function VideoPlayerStateContextProvider({ children, videoKey, useVideoFromArticle = true }: VideoPlayerStateContextProviderProps) {
    const externalVideoState = usePlayedVideo();
    const registeredVideoState = useRegisteredVideo(videoKey);

    return (
        <VideoPlayerStateContext.Provider value={useVideoFromArticle ? externalVideoState : registeredVideoState}>
            {children}
        </VideoPlayerStateContext.Provider>
    );
}

/** @public */
export function useRegisteredVideo(videoKey = 'default') {
    return useSessionData('videoState-' + videoKey);
}
